/// <amd-module name="Wiki/Overview/Actions/PageTreeActionCreator" />

import { format, startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { StateMergeOptions } from "VSS/Platform/History";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { IButtonProps } from "VSSUI/Button";
import * as VCContracts from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiPageMoveResponse, WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { showMoveConfirmationDialogOrPanel } from "Wiki/Overview/OverviewDialogHandler";
import * as Resources from "Wiki/Overview/Resources";
import { WikiPageSource } from "Wiki/Overview/Sources/WikiPageSource";
import { OverviewActions, StoresHub } from "Wiki/Overview/Stores/StoresHub";
import { PagesHierarchyFetchedStatus } from "Wiki/Overview/Stores/WikiTreeStore";
import { ingestPageView, isFolderAssociatedPage } from "Wiki/Overview/Utils/WikiHelper";
import { WikiPageSwitchTtiLogger } from "Wiki/Overview/Utils/WikiPageSwitchTtiLogger";
import { wikiPageFiltered } from "Wiki/Overview/Utils/WikiTelemetryHelper";
import { WikiPageFilterProvider, WikiPageSearchResults } from "Wiki/PageTree/Utilities/WikiPageFilterProvider";
import { ActionIds, RepoConstants, TemplateConstants } from "Wiki/ViewCommon/Constants";
import { WikiPageInfo, WikiPagesOnlyDataProviderData } from "Wiki/ViewCommon/Contracts";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getFileName, getParentPath } from "Wiki/ViewCommon/Utils/PathHelper";
import { boundedOrderIncrement, isCodeEditEnabled, publish, updateUrl } from "Wiki/ViewCommon/Utils/WikiHelper";

export class PageTreeActionCreator {
    private _allPagesLoadPromise: Promise<WikiPage[]>;
    private _currentDragSource: WikiPage | undefined;
    private _filterProvider: WikiPageFilterProvider;

    constructor(
        private _pageContext: IVssPageContext,
        private _wiki: WikiV2,
        private _version: VCContracts.GitVersionDescriptor,
        private _source: WikiPageSource,
        private _overviewActions: OverviewActions,
        private _stores: StoresHub,
        private _wikiPageSwitchTtiLogger: WikiPageSwitchTtiLogger,
        private _loadHeaderMetadata: (pagePath: string) => void
    ) {
        // currently filtering is always supported, no harm in initializing it always
        this._filterProvider = new WikiPageFilterProvider(this._pageContext, "overview-tree", this.getPagesForFiltering, this._onError);
    }

    public loadTreeIfRequired = (): void => {
        if (!isFeatureFlagEnabled(this._pageContext, "Wiki.DoNotLoadPartialTreeAsync")) {
            const pageInfo = this._stores.getState().overviewState.pageInfo;
            if (pageInfo && this._stores.getState().treeState.pagesFetchedStatus === PagesHierarchyFetchedStatus.None) {
                this._source
                    .getInitialTreeHierarchy(this._pageContext, this._wiki, this._version, pageInfo.path)
                    .then((data: WikiPagesOnlyDataProviderData) => {
                        this._overviewActions.partialPagetreeLoaded.invoke({
                            pagesFetched: data.pages,
                            pathToSelect: pageInfo.path
                        });
                    });
            }
        }

        // meanwhile also make a call to load the entire hierarchy
        this.loadAllPages();
    };

    /*
     * Fetches all page hierarchy once from server
     * If already fetched, returns previously fetched pages
     * this promise will return all the pages at a given point - until the first response.
     * it will NOT contain pages added through UX after the response
     */
    public loadAllPages = (): Promise<WikiPage[]> => {
        if (!this._allPagesLoadPromise) {
            this._allPagesLoadPromise = this._source.getAllPages(this._wiki, this._version);
            const currentSelectedPage = this._stores.getState().overviewState.pageInfo;
            this._allPagesLoadPromise.then(pagesFetched => {
                this._overviewActions.allPagesFetched.invoke({
                    pagesFetched,
                    pathToSelect: currentSelectedPage ? currentSelectedPage.path : undefined
                });
            });
        }

        return this._allPagesLoadPromise;
    };

    public onPageSelected = (newPage?: WikiPage, updateTree?: boolean, skipUpdateUrl?: boolean, anchor?: string): void => {
        this._onPageSelected(newPage, updateTree, skipUpdateUrl, anchor);
    };

    public makePageAsSubPage = (
        wiki: WikiV2,
        version: VCContracts.GitVersionDescriptor,
        sourcePage: WikiPage,
        parentPage: WikiPage | undefined,
        newOrderInParent?: number,
        throwError?: boolean
    ): Promise<WikiPageMoveResponse | void> => {
        const parentPath = parentPage ? parentPage.path : RepoConstants.RootPath;
        const defaultOrder = parentPath === RepoConstants.RootPath ? 1 : 0;

        const pageName = getFileName(sourcePage.path);
        const newPagePath = combineUrlPaths(parentPath, pageName);
        const originalPath = sourcePage.path;

        const newOrder = newOrderInParent == null ? defaultOrder : newOrderInParent;

        const comment = format(Resources.MovePageDefaultComment, pageName, getParentPath(sourcePage.path), parentPath);

        return this._source.movePage(wiki, version, newPagePath, originalPath, newOrder, comment).then(
            (pageMoveResponse: WikiPageMoveResponse) => {
                this._overviewActions.onPageMoveCompleted.invoke({ sourcePage: sourcePage, pageMoveResponse: pageMoveResponse });
                this._onPageSelected(pageMoveResponse.pageMove.page);
                // a lot has changed for child objects(path, gititempath, subpages path...) - Fetch them again
                this.reloadPage(newPagePath);
                publish(this._pageContext, TelemetryConstants.PageMoved, wiki.type, { pageId: pageMoveResponse.pageMove.page.id });
                this.onFilteringDataSetChanged();
                return pageMoveResponse;
            },
            (e: Error) => {
                if (throwError) {
                    throw e;
                } else {
                    this._onError(e);
                }
            }
        );
    };

    public reloadPage = (path: string): void => {
        this._source.getPageWithHierarchy(this._wiki, this._version, path).then((pages: WikiPage[]) => {
            this._overviewActions.pageHierarchyReloaded.invoke({ pagePath: path, flattenedHierarchy: pages });
        });
    };

    public onPageToggled = (page: WikiPage, isExpanded?: boolean): void => {
        // on collapse or expand for already fetched items - just expand collapse
        if (!isExpanded || (page.isParentPage && page.subPages && page.subPages.length)) {
            this._overviewActions.treeItemToggled.invoke({ pagePath: page.path, isExpanded: isExpanded });
        } else if (page.isParentPage) {
            // for items expanded whose child items are not yet fetched, fetch
            this._source
                .getPageChildren(this._wiki, this._version, page)
                .then(childPages => this._overviewActions.childPagesFetched.invoke({ parentPath: page.path, childPages: childPages }));
        }
    };

    public onFilteringTextChanged = (filterText: string): void => {
        this._overviewActions.onFilterTextUpdated.invoke(filterText); // instead it will re-evaluate results
        this._filter(filterText);
    };

    public onFilteringComplete = (): void => {
        const pageInfo = this._stores.getState().overviewState.pageInfo;
        this._overviewActions.onFilteringComplete.invoke({ lastSelectedPage: pageInfo && pageInfo.path });
    };

    public onFilteringDataSetChanged(): void {
        const text = this._stores.getState().filterState.filterText;
        if (text) {
            this._filter(text);
        }
    }

    public canDrag = (source: WikiPage): boolean => {
        // TODO: disallow for no permission, edit/new page scenarios
        return (
            (this._wiki.type === WikiType.ProjectWiki || isCodeEditEnabled(this._pageContext)) &&
            !source.isNonConformant &&
            source.path !== TemplateConstants.TemplateDirectory
        );
    };

    public onDrag = (source: WikiPage): void => {
        this._currentDragSource = source;
    };

    public onDrop = (target: WikiPage, isHandleBrokenLinksEnabled?: boolean, isReorderOperation?: boolean, isReorderAbove?: boolean): void => {
        const sourcePage = this._currentDragSource;
        this._currentDragSource = undefined;

        if (sourcePage) {
            if (isReorderOperation) {
                this.reorderPage(sourcePage, target, isReorderAbove, isHandleBrokenLinksEnabled);
            } else {
                this._confirmMovePage(sourcePage, target, 0, isHandleBrokenLinksEnabled);
            }
        }
    };

    public canDrop = (target: WikiPage, isReorderOperation?: boolean): boolean => {
        return this._currentDragSource ? this._isReorderOrReparentPagesAllowed(this._currentDragSource, target, isReorderOperation) : false;
    };

    public reorderPage = (sourcePage: WikiPage, siblingPage: WikiPage, isReorderAbove?: boolean, isHandleBrokenLinksEnabled?: boolean): void => {
        if (!sourcePage || !siblingPage || !sourcePage.path || !siblingPage.path || sourcePage.path === siblingPage.path) {
            // source and target are same pages and hence it is a no-op
            return;
        }

        const sourceParentPath = getParentPath(sourcePage.path);
        const targetParentPath = getParentPath(siblingPage.path);

        if (sourceParentPath === targetParentPath) {
            // Same parent, so reorder only change
            this._makePageAsSibling(sourcePage, siblingPage, isReorderAbove);
        } else {
            // TODO: figure out when can siblingPage.order be undefined
            // Different parents, so both reorder and reparent change
            const newOrderInParent = isReorderAbove ? siblingPage.order || 0 : siblingPage.order ? boundedOrderIncrement(siblingPage.order) : 1;
            const parentPage = this._stores.getState().treeState.treeItemProvider.getItemFromTree(targetParentPath);
            this._confirmMovePage(sourcePage, parentPage, newOrderInParent, isHandleBrokenLinksEnabled || false);
        }
    };

    public getPagesForFiltering = (): Promise<WikiPage[]> => {
        if (this._stores.stores.treeStore.getState().pagesFetchedStatus === PagesHierarchyFetchedStatus.Complete) {
            return Promise.resolve(this._stores.stores.treeStore.getState().treeItemProvider.getAllPages());
        }

        return this.loadAllPages();
    };

    public moveTreeItemOneLevel(above: boolean): void {
        const currentState = this._stores.getState();
        if (currentState.overviewState.pageInfo) {
            const currentPath = currentState.overviewState.pageInfo.path;
            // Moving of template directory is not allowed.
            if (currentPath === TemplateConstants.TemplateDirectory) {
                return;
            }

            const page = currentState.treeState.treeItemProvider.getItemFromTree(currentPath);
            if (page) {
                const siblingPage = currentState.treeState.treeItemProvider.getNextSibling(page.path, above);
                siblingPage && this.reorderPage(page, siblingPage, above);
            }
        }
    }

    private _onError = (e: Error): void => {
        this._overviewActions.onErrorChanged.invoke(e.message);
    };

    // TODO: must unit test
    private _filter = (filterText: string): void => {
        this._filterProvider.filter(filterText).then((results: WikiPageSearchResults) => {
            wikiPageFiltered(this._pageContext, this._wiki.type);
            const pageInfo = this._stores.getState().overviewState.pageInfo;
            this._overviewActions.onFilterResultsUpdated.invoke({
                filteredPages: results.results,
                exactMatchedPagesCount: results.exactMatchedPagesCount,
                currentSelectedPagePath: pageInfo && pageInfo.path
            });
        });
    };

    private _confirmMovePage(
        sourcePage: WikiPage,
        parentPage: WikiPage | undefined,
        newOrderInParent: number,
        isHandleBrokenLinksEnabled?: boolean
    ): void {
        let targetPath = parentPage ? parentPage.path : "";
        targetPath += RepoConstants.PathSeparator + getFileName(sourcePage.path);
        const onSavePromise = () => {
            return this.makePageAsSubPage(this._wiki, this._version, sourcePage, parentPage, newOrderInParent, true);
        };
        const brokenLinksProps = {
            onConfirm: onSavePromise,
            newPagePath: targetPath,
            oldPagePath: sourcePage.path,
            wiki: this._wiki,
            wikiVersion: this._version,
            title: format(Resources.MovePagePanel_BrokenLinkFixing_Title, getFileName(sourcePage.path)),
            description: format(Resources.MovePagePanel_BrokenLinkFixing_Description, getParentPath(sourcePage.path), getParentPath(targetPath)),
            successMessage: Resources.MovePagePanel_BrokenLinkFixing_SuccessMessage,
            brokenLinksDiscoveryOrRenameFailureMessage: Resources.MovePagePanel_BrokenLinkFixing_BrokenLinksDiscoveryOrMoveFailureMessage,
            linksFixFailureMessage: Resources.MovePagePanel_BrokenLinkFixing_LinksFixFailureMessage,
            updateButtonText: Resources.MovePagePanel_MoveButtonText
        };

        showMoveConfirmationDialogOrPanel(
            this._pageContext,
            brokenLinksProps,
            {
                onSave: onSavePromise,
                wikiType: this._wiki.type
            },
            isHandleBrokenLinksEnabled
        );
    }

    private _isReorderOrReparentPagesAllowed(sourcePage: WikiPage, targetPage: WikiPage, isReorderOperation?: boolean): boolean {
        if (this._stores.getState().overviewState.isEditing) {
            return false;
        }
        if (!sourcePage || sourcePage.isNonConformant || !targetPage || targetPage.isNonConformant || sourcePage.path === targetPage.path) {
            return false;
        }

        const isTargetDescendantOfSource = startsWith(targetPage.path, sourcePage.path + RepoConstants.RootPath);
        const isTargetImmediateParentOfSource = getParentPath(sourcePage.path) === targetPage.path;

        if (isReorderOperation) {
            return !isTargetDescendantOfSource;
        } else {
            return !isTargetImmediateParentOfSource && !isTargetDescendantOfSource;
        }
    }

    private _makePageAsSibling(sourcePage: WikiPage, siblingPage: WikiPage, isReorderAbove?: boolean): void {
        sourcePage.order = sourcePage.order || 0;
        siblingPage.order = siblingPage.order || 0;
        const originalOrder = sourcePage.order || 0;
        let intendedOrder = 0;

        if (isReorderAbove) {
            intendedOrder = sourcePage.order > siblingPage.order ? siblingPage.order : siblingPage.order - 1;
        } else {
            intendedOrder = sourcePage.order > siblingPage.order ? boundedOrderIncrement(siblingPage.order) : siblingPage.order;
        }

        if (intendedOrder === originalOrder) {
            return;
        }

        this._overviewActions.pageReorderStarted.invoke();
        const pagePath = sourcePage.path;
        this._source.reorderPage(this._wiki, this._version, pagePath, intendedOrder).then((page: WikiPage) => {
            let errorMessage: string | undefined = undefined;
            let errorButtonProps: IButtonProps[] | undefined = undefined;
            if (intendedOrder !== page.order) {
                publish(this._pageContext, TelemetryConstants.IncompleteOrderFileDetected, this._wiki.type);

                errorMessage = Resources.IncompleteOrderFileErrorMessage;
                errorButtonProps = [
                    {
                        text: Resources.IncompleteOrderFileOpenDocs,
                        href: Resources.IncompleteOrderFileErrorLink,
                        target: "_blank"
                    }
                ];
            }

            this._overviewActions.pageReordered.invoke({
                pagePath,
                newOrder: page.order, // page.order can be different from the newOrder we sent to the call in case order file is mising
                nextPageToSelect: pagePath,
                oldOrder: originalOrder,
                errorButtonProps: errorButtonProps,
                errorMessage: errorMessage
            });

            this._onPageSelected(sourcePage, undefined, undefined, undefined, true);
            this.onFilteringDataSetChanged();
        }, this._onError);
    }

    /**
     * internal method that makes source call, without invoking any new action in sync flow
     */

    private _onPageSelected = (
        newPage?: WikiPage,
        updateTree?: boolean,
        skipUpdateUrl?: boolean,
        anchor?: string,
        isSystemAction?: boolean
    ): void => {
        // new page can be undefined in some cases like back navigation to broken page
        if (!newPage) {
            this._overviewActions.pageSelected.invoke({ wikiPage: newPage, anchor, updateTree, isSystemAction });
            return;
        }
        // Start page switch overview performance scenario.
        this._wikiPageSwitchTtiLogger.startScenario();
        this._updateUrlIfNeeded(newPage.path, undefined, ActionIds.View, false, skipUpdateUrl);
        this._loadHeaderMetadata(newPage.path);
        this.onPageToggled(newPage, true);

        if (newPage.isNonConformant || isFolderAssociatedPage(newPage)) {
            this._overviewActions.pageInfoLoaded.invoke({
                wikiPageInfo: {
                    ...newPage,
                    version: undefined,
                    content: ""
                },
                isSystemAction: isSystemAction
            });
            if (newPage.isNonConformant) {
                publish(this._pageContext, TelemetryConstants.NonConformantPageSelected, this._wiki.type, { path: newPage.path });
            }
        } else {
            this._overviewActions.pageSelected.invoke({ wikiPage: newPage, anchor, updateTree, isSystemAction });
            this._source.getPageInfo(this._wiki, newPage.path, this._version).then((wikiPageInfo: WikiPageInfo) => {
                const pageInfo = this._stores.getState().overviewState.pageInfo;
                if (pageInfo && pageInfo.path === newPage.path) {
                    this._updateUrlIfNeeded(wikiPageInfo.path, wikiPageInfo.id, ActionIds.View, true, skipUpdateUrl);
                    this._overviewActions.pageInfoLoaded.invoke({ wikiPageInfo: wikiPageInfo, isSystemAction: isSystemAction });
                    ingestPageView(this._pageContext, this._wiki.id, wikiPageInfo.id, wikiPageInfo.path);
                }
            }, this._onError);
        }
    };

    private _updateUrlIfNeeded = (
        pagePath: string,
        pageId: number | undefined,
        action: string,
        replaceState?: boolean,
        skipUpdateUrl?: boolean
    ): void => {
        if (!skipUpdateUrl) {
            updateUrl(
                this._pageContext,
                {
                    _a: action,
                    pagePath: pagePath,
                    pageId: pageId
                },
                undefined,
                StateMergeOptions.routeValues,
                replaceState
            );
        }
    };
}
